import { createApi, createEffect, createStore } from "effector";
import { persist } from "effector-storage/session";

import { QuoteStore } from "./types";
import { defaultState } from "./utils";
import { FullQuote } from "@hunkware/shared/types";
import { quoteCreate } from "../../../shared/api";
import { resetAccountForLocationChange } from "../../account";

export const quoteCreateFx = createEffect(quoteCreate);

export const $quote = createStore<QuoteStore>({ ...defaultState })
  .on(quoteCreateFx.done, (state, response) => ({
    ...state,
    quote: response.result.data.quotes[0],
  }))
  .on(quoteCreateFx.pending, (state, loading) => ({ ...state, loading }))
  // A quote created for the old location is meaningless once the user's zip
  // resolves to a different one (mirrors resetAccountForLocationChange's own
  // gating — already-had-a-location + new one differs — see account/store.ts)
  // — without this, a stale quote.id from the old location would make
  // use-book-on-site-estimate.ts's `if (!quote?.id)` guard skip creating a
  // fresh quote for the new one entirely.
  .on(resetAccountForLocationChange, () => ({ ...defaultState }));

persist<QuoteStore>({
  store: $quote,
  key: "booking-v4-quote",
  def: { ...defaultState },
});

export const quoteApi = createApi($quote, {
  set: (prevState, quote: Partial<FullQuote>): QuoteStore => ({
    ...prevState,
    quote: {
      ...prevState.quote,
      ...quote,
      // Deep-merge nested location objects so fields written by different steps
      // (e.g. destination address from destination-location and dwelling details
      // from destination-dwelling-information) don't overwrite each other.
      ...(quote.origin != null && {
        origin: { ...prevState.quote?.origin, ...quote.origin },
      }),
      ...(quote.destination != null && {
        destination: { ...prevState.quote?.destination, ...quote.destination },
      }),
    },
  }),

  reset: () => ({ ...defaultState }),
});
